import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const approach = [
  "Keep things simple",
  "Focus on what actually matters",
  "Turn information into practical decisions"
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="py-28 md:py-36 relative" data-testid="about-hero">
        {/* Background elements */}
        <div className="absolute top-20 right-12 w-48 h-48 border-2 border-[#1565C0]/10 hidden lg:block" />
        <div className="absolute bottom-40 right-32 w-24 h-24 bg-[#00897B]/10 hidden lg:block" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="accent-bar"></div>
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#1565C0]">About</p>
            </div>
            
            <h1 className="font-['Cabinet_Grotesk',sans-serif] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#0D1B2A] leading-[1.15] mb-12" data-testid="about-heading">
              I have spent my career understanding how businesses work. Today, I use that experience to help business owners find clarity, make better decisions and move forward with confidence.
            </h1>

            <div className="space-y-8 text-lg md:text-xl text-[#3D5A73] leading-relaxed" data-testid="about-content">
              <p>
                Over time, I have seen how decisions are made at different levels. Large organisations often have data but struggle to act quickly. Small businesses move fast but often lack clarity around their numbers.
              </p>
              
              <p className="text-2xl md:text-3xl gradient-text font-['Cabinet_Grotesk',sans-serif] font-bold py-4">
                That gap is where most problems sit.
              </p>

              <p>
                I have spent more than 20 years working across finance, business development, and leadership roles. This includes working with businesses across Australia and Asia, managing large portfolios, and helping teams improve performance.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 bg-[#F0F4F8] border-t border-[#E0E7ED]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left - Sticky Heading */}
            <motion.div 
              className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="accent-bar mb-6"></div>
              <h2 className="font-['Cabinet_Grotesk',sans-serif] text-3xl md:text-4xl font-bold tracking-tight text-[#0D1B2A]" data-testid="about-subheading">
                What shaped my thinking is simple.
              </h2>
            </motion.div>

            {/* Right - Content */}
            <div className="lg:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-xl text-[#3D5A73] leading-relaxed mb-12">
                  Most businesses do not fail because they lack effort. They struggle because they do not have a clear view of what is working and what is not.
                </p>

                {/* Approach Box */}
                <div className="highlight-box p-10 md:p-12 mb-12">
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00897B] mb-8">My approach is straightforward</p>
                  <div className="space-y-6">
                    {approach.map((item, index) => (
                      <motion.div 
                        key={index}
                        className="flex items-center gap-6 py-4 border-b border-white/10 last:border-0"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        data-testid={`approach-item-${index}`}
                      >
                        <span className="w-10 h-10 rounded-full bg-[#00897B] flex items-center justify-center text-white font-bold text-sm">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="text-xl text-white font-medium">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <p className="text-lg text-[#3D5A73] leading-relaxed">
                  When I work with someone, they can expect honest conversations, clear thinking, and no unnecessary complexity.
                </p>
              </motion.div>

              {/* CTA */}
              <motion.div 
                className="mt-12"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-3 text-[#1565C0] font-bold hover:text-[#0D47A1] transition-all"
                  data-testid="about-cta"
                >
                  Let's have a conversation <ArrowRight size={18} weight="bold" className="arrow-animate" />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
